export type RepaymentType = 'PI' | 'IO'

/**
 * Monthly loan repayment — standard annuity formula.
 * P&I:  M = P * (r*(1+r)^n) / ((1+r)^n - 1)
 * IO:   M = P * r
 *
 * `ratePercent` is a percentage (e.g. 5.79, not 0.0579). Returns 0 for any
 * non-positive input so callers can render a safe $0 while a field is empty.
 */
export function monthlyRepayment(
  principal: number,
  ratePercent: number,
  years: number,
  type: RepaymentType,
): number {
  const r = ratePercent / 100 / 12
  const n = years * 12
  if (principal <= 0 || r <= 0 || n <= 0) return 0
  if (type === 'IO') return principal * r
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1)
}

export type ExtraFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'lumpSum'

/** Convert an extra-repayment amount at a given frequency into an equivalent
 * monthly top-up (plus a one-off lump sum, applied in month 1). */
export function toMonthlyExtra(amount: number, frequency: ExtraFrequency): { extraMonthly: number; lumpSum: number } {
  if (!amount || amount <= 0) return { extraMonthly: 0, lumpSum: 0 }
  switch (frequency) {
    case 'weekly':
      return { extraMonthly: (amount * 52) / 12, lumpSum: 0 }
    case 'fortnightly':
      return { extraMonthly: (amount * 26) / 12, lumpSum: 0 }
    case 'lumpSum':
      return { extraMonthly: 0, lumpSum: amount }
    case 'monthly':
    default:
      return { extraMonthly: amount, lumpSum: 0 }
  }
}

// Hard cap so a payment that never clears the loan can't loop forever.
const MAX_MONTHS = 1200

/**
 * Month-by-month amortisation simulator. Interest each month is charged on the
 * balance MINUS any offset balance (so an offset reduces interest while the
 * repayment stays the same, paying the loan down faster). `lumpSum` is applied
 * up front. Returns whole months to payoff + total interest paid.
 */
export function simulateLoan(
  principal: number,
  ratePercent: number,
  monthlyPayment: number,
  extraMonthly = 0,
  lumpSum = 0,
  offsetBalance = 0,
): { months: number; totalInterest: number } {
  const r = ratePercent / 100 / 12
  let balance = principal - Math.max(0, lumpSum)
  if (balance <= 0) return { months: 0, totalInterest: 0 }
  if (r <= 0 || monthlyPayment <= 0) return { months: 0, totalInterest: 0 }

  let totalInterest = 0
  let months = 0
  const pay = monthlyPayment + Math.max(0, extraMonthly)
  while (balance > 0 && months < MAX_MONTHS) {
    const net = Math.max(0, balance - Math.max(0, offsetBalance))
    const interest = net * r
    totalInterest += interest
    balance = balance + interest - pay
    months += 1
    // Payment never covers the interest -> loan won't clear; bail at the cap.
    if (pay <= interest && offsetBalance <= 0) break
  }
  return { months, totalInterest }
}

/** Interest + time saved by making extra repayments vs the standard schedule. */
export function extraRepaymentImpact(
  loan: number,
  ratePercent: number,
  years: number,
  extraAmount: number,
  frequency: ExtraFrequency,
): { baseMonths: number; baseInterest: number; newMonths: number; newInterest: number; interestSaved: number; monthsSaved: number } {
  const base = monthlyRepayment(loan, ratePercent, years, 'PI')
  const baseline = simulateLoan(loan, ratePercent, base)
  const { extraMonthly, lumpSum } = toMonthlyExtra(extraAmount, frequency)
  const withExtra = simulateLoan(loan, ratePercent, base, extraMonthly, lumpSum)
  return {
    baseMonths: baseline.months,
    baseInterest: baseline.totalInterest,
    newMonths: withExtra.months,
    newInterest: withExtra.totalInterest,
    interestSaved: Math.max(0, baseline.totalInterest - withExtra.totalInterest),
    monthsSaved: Math.max(0, baseline.months - withExtra.months),
  }
}

/** Interest + time saved by keeping a balance in an offset account. */
export function offsetImpact(
  loan: number,
  ratePercent: number,
  years: number,
  offsetBalance: number,
): { baseInterest: number; newInterest: number; interestSaved: number; monthsSaved: number; newMonths: number } {
  const base = monthlyRepayment(loan, ratePercent, years, 'PI')
  const baseline = simulateLoan(loan, ratePercent, base)
  const withOffset = simulateLoan(loan, ratePercent, base, 0, 0, offsetBalance)
  return {
    baseInterest: baseline.totalInterest,
    newInterest: withOffset.totalInterest,
    interestSaved: Math.max(0, baseline.totalInterest - withOffset.totalInterest),
    monthsSaved: Math.max(0, baseline.months - withOffset.months),
    newMonths: withOffset.months,
  }
}

/** Human "Xy Ym" from a whole number of months. */
export function formatMonths(months: number): string {
  const y = Math.floor(months / 12)
  const m = months % 12
  if (y && m) return `${y} year${y > 1 ? 's' : ''} and ${m} month${m > 1 ? 's' : ''}`
  if (y) return `${y} year${y > 1 ? 's' : ''}`
  return `${m} month${m !== 1 ? 's' : ''}`
}
